import { useState, useEffect } from "react";
import {
  FileText,
  Upload,
  MapPin,
  DollarSign,
  Image as ImageIcon,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

import { SharetribeRepository } from "../infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { secureStorage } from "@/utils/secureStorage";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alertDialog";

export function ApplyToCoachContent() {
  const [photos, setPhotos] = useState<File[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<any>(null);
  const [resultDialog, setResultDialog] = useState<any>(null);

  const [existingListing, setExistingListing] = useState<any>(null);
  const [loadingListing, setLoadingListing] = useState(true);
  console.log(loadingListing)
  const [name, setName] = useState(secureStorage.get("current_user_name") || "");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [priceCurrency, setPriceCurrency] = useState("USD");
  const [priceAmount, setPriceAmount] = useState<number | null>(null);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) setPhotos([...photos, ...Array.from(files)]);
  };


  /* -------------------------------------------------------------
    LOAD LISTING
  ------------------------------------------------------------- */
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const repo = new SharetribeRepository(apiService);

        const result = await repo.ownListingsQuery({}, ["images"]);
        const data = result.data;
        const included = result.included;

        if (data && data.length > 0) {
          const lst = data.find(
            (item: any) => item?.attributes?.publicData?.listingType === "service"
          );

          if (lst) {
            setExistingListing({
              ...lst,
              includedImages: included,
            });
          }
        }
      } catch (err) {
        console.error("Error loading coach listing", err);
      } finally {
        setLoadingListing(false);
      }
    };

    fetchListing();
  }, []);

  /* -------------------------------------------------------------
    PREFILL FIELDS
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!existingListing) return;

    const attrs = existingListing.attributes;

    setName(attrs.title || "");
    setDescription(attrs.description || "");
    setLocation(attrs.publicData?.location?.address || "");

    if (attrs.price?.amount) {
      setPriceAmount(attrs.price.amount / 100);
    }

    setPriceCurrency(attrs.price?.currency || "USD");
  }, [existingListing]);

  /* -------------------------------------------------------------
    INTERNAL SUBMIT FUNCTION
  ------------------------------------------------------------- */
  async function handleSubmitInternal() {
    try {
      const repo = new SharetribeRepository(apiService);
      const uploadedImageIds: string[] = [];

      // upload images
      for (const file of photos) {
        const uploadResp = await repo.uploadImage(file, file.type);
        if (uploadResp?.data?.id) uploadedImageIds.push(uploadResp.data.id);
      }

      const publicData = {
        listingType: "service",
        rule: "",
        location: {
          address: location,
          building: "",
        },
      };

      const priceInCents = Math.round(Number(priceAmount || 0) * 100);

      let resp = null;
      console.log(resp)
      if (existingListing) {
        resp = await repo.ownListingsUpdate(
          existingListing.id,
          null,
          description.trim(),
          0,
          0,
          null,
          priceInCents,
          uploadedImageIds,
          null,
          null,
          publicData,
          ["images"]
        );
      } else {
        const availabilityPlan = {
          type: "availability-plan/day",
          entries: [
            { dayOfWeek: "mon", seats: 99999 },
            { dayOfWeek: "tue", seats: 99999 },
            { dayOfWeek: "wed", seats: 99999 },
            { dayOfWeek: "thu", seats: 99999 },
            { dayOfWeek: "fri", seats: 99999 },
            { dayOfWeek: "sat", seats: 99999 },
            { dayOfWeek: "sun", seats: 99999 },
          ],
        };

        resp = await repo.ownListingsCreate(
          name.trim(),
          description.trim(),
          0,
          0,
          null,
          priceInCents,
          uploadedImageIds,
          availabilityPlan,
          null,
          publicData,
          ["images"]
        );
      }

      return true;
    } catch (err) {
      console.error("Submit failed:", err);
      return false;
    }
  }

  /* -------------------------------------------------------------
    OPEN LISTING
  ------------------------------------------------------------- */
  async function runOpenListing() {
    const repo = new SharetribeRepository(apiService);
    await repo.ownListingsOpen(existingListing.id);
  }

  /* -------------------------------------------------------------
    CLOSE LISTING
  ------------------------------------------------------------- */
  async function runCloseListing() {
    const repo = new SharetribeRepository(apiService);
    await repo.ownListingsClose(existingListing.id);
  }

  /* -------------------------------------------------------------
    UNIFIED ACTION HANDLER
  ------------------------------------------------------------- */
  async function runAction(type: string) {
    setConfirmDialog(null);

    try {
      const repo = new SharetribeRepository(apiService);

      if (type === "open") {
        await runOpenListing();
        setResultDialog({ success: true, message: "Listing opened successfully!" });
      }

      if (type === "close") {
        await runCloseListing();
        setResultDialog({ success: true, message: "Listing closed successfully!" });
      }

      if (type === "submit" || type === "update") {
        const ok = await handleSubmitInternal();

        setResultDialog({
          success: ok,
          message: ok
            ? type === "submit"
              ? "Application submitted successfully!"
              : "Application updated successfully!"
            : "Something went wrong. Please try again.",
        });
      }

      // reload listing
      const refreshed = await repo.ownListingsQuery({}, ["images"]);
      const updated = refreshed.data.find(
        (i: any) => i?.attributes?.publicData?.listingType === "service"
      );
      setExistingListing(updated || null);
    } catch (err) {
      console.error(err);
      setResultDialog({ success: false, message: "Something went wrong." });
    }
  }

  /* -------------------------------------------------------------
    RENDER UI
  ------------------------------------------------------------- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Apply to Coach</h1>
        <p className="text-muted-foreground">Complete your coach application to start training others</p>
      </div>

      {/* ============================== */}
      {/* Coach Listing Card */}
      {/* ============================== */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="mr-2 h-5 w-5" />
            Coach Listing Details
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-sm font-medium">Name</label>
            <Input placeholder="Enter Coach Name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium">Description</label>
            <Textarea
              placeholder="Describe your coaching services..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Location */}
          <div>
            <label className="text-sm font-medium flex items-center">
              <MapPin className="h-4 w-4 mr-2" /> Location
            </label>
            <Input placeholder="Enter location/address..." value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>

          {/* Price */}
          <div>
            <label className="text-sm font-medium flex items-center">
              <DollarSign className="h-4 w-4 mr-2" /> Price
            </label>
            <div className="flex space-x-2">
              <Input
                placeholder="Currency"
                value={priceCurrency}
                onChange={(e) => setPriceCurrency(e.target.value)}
              />
              <Input
                type="number"
                placeholder="Amount"
                value={priceAmount || ""}
                onChange={(e) => setPriceAmount(Number(e.target.value))}
              />
            </div>
          </div>

          {/* Photos */}
          <div>
            <label className="text-sm font-medium flex items-center">
              <ImageIcon className="h-4 w-4 mr-2" /> Upload Photo
            </label>

            <input
              type="file"
              multiple
              id="photoUpload"
              className="hidden"
              onChange={handlePhotoUpload}
            />

            <Button variant="outline" onClick={() => document.getElementById("photoUpload")?.click()}>
              <Upload className="h-4 w-4 mr-2" /> Upload Photos
            </Button>

            {photos.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {photos.map((file, i) => (
                  <div key={i} className="border rounded-lg p-2 text-sm text-muted-foreground truncate">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ============================== */}
      {/* Bottom Buttons */}
      {/* ============================== */}
      <div className="flex items-center justify-between">
        {existingListing && existingListing.attributes.state === "closed" && (
          <Button variant="default" onClick={() => setConfirmDialog({ type: "open" })}>
            Open Listing
          </Button>
        )}

        {existingListing && existingListing.attributes.state === "published" && (
          <Button variant="destructive" onClick={() => setConfirmDialog({ type: "close" })}>
            Close Listing
          </Button>
        )}

        <Button onClick={() => setConfirmDialog({ type: existingListing ? "update" : "submit" })}>
          {existingListing ? "Update Application" : "Submit Application"}
        </Button>
      </div>

      {/* ============================== */}
      {/* CONFIRMATION DIALOG */}
      {/* ============================== */}
      {confirmDialog && (
        <AlertDialog open={true}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmDialog.type === "open" && "Open this listing and make it visible to users?"}
                {confirmDialog.type === "close" && "Close this listing? Users will not be able to see it."}
                {confirmDialog.type === "submit" && "Submit your coach application?"}
                {confirmDialog.type === "update" && "Update your coach application?"}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmDialog(null)}>
                Cancel
              </AlertDialogCancel>

              <AlertDialogAction onClick={() => runAction(confirmDialog.type)}>
                Yes, Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* ============================== */}
      {/* RESULT DIALOG */}
      {/* ============================== */}
      {resultDialog && (
        <AlertDialog open={true}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {resultDialog.success ? "Success" : "Error"}
              </AlertDialogTitle>
              <AlertDialogDescription>{resultDialog.message}</AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setResultDialog(null)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
